import { inject } from "@ember/service"
import Component from "@glimmer/component"
import type { StandardSet } from "@shared/models/csp-jurisdiction"
import type EmberStore from "cc-frontend/services/ember-store"
import type Rpc from "cc-frontend/services/rpc"
import { cloneDeep, includes, reduce } from "lodash-es"
import * as R from "remeda"
import { tracked } from "tracked-built-ins"
function isEven(n) {
  return n % 2 == 0
}

interface Signature {
  Args: {
    standardSet: StandardSet
  }
}

export default class AdminCspStandardSet extends Component<Signature> {
  @inject("ember-store") declare store: EmberStore
  @inject declare rpc: Rpc

  @tracked isSaving = false
  @tracked selectionCount = 1
  @tracked startingSelectionPosition: null | number = null
  @tracked endingSelectionPosition: null | number = null
  @tracked localStandardSet: null | StandardSet = null

  @tracked selectedStandardIds: Array<string> = []

  get isSelectionHappening() {
    return this.startingSelectionPosition !== 0 && this.endingSelectionPosition !== 0
  }

  // this isn't entirely accurate -- you could have no changes.
  get hasChanges() {
    return this.localStandardSet !== null
  }

  get standardSet() {
    return this.localStandardSet || this.args.standardSet
  }

  set standardSet(value) {
    this.localStandardSet = value
  }

  get standards() {
    return R.pipe(
      Object.values(this.standardSet.attributes.standards),
      R.sortBy((standard) => standard.position)
    )
  }

  cancelSelect = () => {
    this.startingSelectionPosition = null
    this.endingSelectionPosition = null
    this.selectedStandardIds = []
  }

  selectStandard = (standard) => {
    // Alternate which we're selecting
    this.selectionCount++
    if (this.startingSelectionPosition === null && this.endingSelectionPosition === null) {
      this.startingSelectionPosition = standard.position
      this.endingSelectionPosition = standard.position
    } else if (isEven(this.selectionCount)) {
      this.startingSelectionPosition = standard.position
    } else {
      this.endingSelectionPosition = standard.position
    }

    let start = this.startingSelectionPosition || 0
    let end = this.endingSelectionPosition || 0

    let selectedIds = R.pipe(
      Object.values(this.standardSet.attributes.standards),
      R.filter((standard) => {
        return standard.position >= start && standard.position <= end
      }),
      R.map((standard) => standard.id)
    )
    this.selectedStandardIds = selectedIds
  }

  isStandardSelected(standard) {
    return includes(this.selectedStandardIds, standard.id)
  }

  deleteStandards = () => {
    let newStandards = reduce(
      this.standardSet.attributes.standards,
      (acc, v, k) => {
        if (this.isStandardSelected(v) === false) {
          acc[k] = v
        }
        return acc
      },
      {}
    )

    let set = cloneDeep(this.standardSet)
    set.attributes.standards = newStandards
    this.standardSet = set
    this.cancelSelect()
  }

  dentStandards = (positiveOrNegative) => {
    let newStandards = reduce(
      this.standardSet.attributes.standards,
      (acc, v, k) => {
        if (this.isStandardSelected(v) === true) {
          let newDepth = v.depth + positiveOrNegative
          v.depth = newDepth < 0 ? 0 : newDepth
        }
        acc[k] = cloneDeep(v)
        return acc
      },
      {}
    )

    let set = cloneDeep(this.standardSet)
    set.attributes.standards = newStandards
    this.standardSet = set
  }

  selectDepth = (depth) => {
    let selectedIds = R.pipe(
      Object.values(this.standardSet.attributes.standards),
      R.filter((standard) => standard.depth === depth),
      R.map((standard) => standard.id)
    )
    this.selectedStandardIds = selectedIds
  }

  saveSet = () => {
    if (window.confirm("Are you sure you want to save these changes?")) {
      this.isSaving = true
      this.rpc
        .admin_csp_update_set({
          standardSet: { id: this.standardSet.id, ...this.standardSet.attributes },
        })
        .then((response) => {
          if (response.responseOk === false) {
            console.log(response)
            window.alert(`Sorry! There was an error! ${response.result.message}`)
          } else {
            this.store.find("standard-set-admin", this.args.standardSet.id, true).then(() => {
              this.isSaving = false
            })
          }
        })
    }
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Admin::Csp::StandardSet": typeof AdminCspStandardSet
  }
}
